<template>
    <div class="ProjectRequestButton w-full">
        <ClientOnly>
            <div v-if="preview">
                <CButton full size="large" variant="success"> Mám záujem o spoluprácu </CButton>
            </div>
            <div v-else>
                <CButton
                    v-if="isProjectOwner"
                    full
                    icon="edit"
                    size="large"
                    :to="{
                        name: 'profile-activity-project-slug',
                        params: { slug: project.slug || project.id }
                    }"
                    variant="primary-150"
                >
                    Upraviť
                </CButton>

                <CButton v-else-if="isLoggedIn && isProjectParticipant" disabled full size="large">
                    Si členom tohto projektu
                </CButton>

                <CButton v-else-if="isLoggedIn && hasPendingRequest" disabled full size="large">
                    Žiadosť čaká na odpoveď
                </CButton>

                <CButton v-else-if="isVolunteeringWeekProjectFull" disabled full size="large">
                    Kapacita je naplnená
                </CButton>

                <CButton v-else full size="large" variant="success" @click="showModal">
                    Mám záujem o spoluprácu
                </CButton>
            </div>
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import type { Org } from '#shared/types/Org'
import type { Project } from '#shared/types/Project'
import { ProjectType } from '#shared/types/Project'
import { getAvailableVolunteeringWeekSpots } from '#shared/utils/volunteeringWeekCapacity'
import RegistrationNotCompletedModal from '@/components/common/modals/RegistrationNotCompletedModal.vue'
import SignInModal from '@/components/modals/SignInModal.vue'
import ProjectRequestModal from '@/components/projects/modals/ProjectRequestModal.vue'
import VolunteerWeekProjectRequestModal from '@/components/projects/modals/VolunteerWeekProjectRequestModal.vue'
import { useFirebase } from '@/composables/firebase/useFirebase'
import { useOrgs } from '@/composables/orgs/useOrgs'
import { useProjects } from '@/composables/projects/useProjects'
import { useCheese } from '@/composables/useCheese'
import type { CompletenessResult } from '@/composables/useCompleteness'
import { useApplicationStore } from '@/stores/application'
import { checkUserProfileComplete } from '@/validators/userProfile'
import { checkVolunteerProfileComplete } from '@/validators/volunteerProfile'
import { computed, onMounted, ref, watch } from 'vue'
import { useModal } from 'vue-final-modal'

const { auth } = useFirebase()
const { doesPendingParticipationRequestExist } = useProjects()
const { canEditOrgOpportunity } = useOrgs()
const { track: trackCheese } = useCheese()

interface Props {
    project: Project
    org: Org
    preview?: boolean
}

const props = defineProps<Props>()
const hasPendingRequest = ref(false)
const store = useApplicationStore()
const isLoggedIn = computed(() => store.isLoggedIn)
const userId = computed(() => store.userId)
const userProfile = computed(() => store.userProfile)
const userPrivateProfile = computed(() => store.userPrivateProfile)

const isProjectParticipant = computed((): boolean => {
    return Boolean(userId.value && props.project.memberIds?.includes(userId.value))
})

const isProjectOwner = computed((): boolean => {
    return canEditOrgOpportunity(userId.value, props.org)
})

const isVolunteeringWeekProjectFull = computed((): boolean => {
    if (props.project.type !== ProjectType.VOLUNTEERING_WEEK) {
        return false
    }

    return (getAvailableVolunteeringWeekSpots(props.project) ?? 0) <= 0
})

onMounted(async () => {
    await checkForPendingRequest()
})

watch(
    () => props.project.id,
    () => {
        if (userId.value) {
            checkForPendingRequest()
        }
    }
)

watch(
    () => userId.value,
    (newVal) => {
        if (newVal) {
            checkForPendingRequest()
        }
    }
)

const { open: openRegistrationNotCompletedModal } = useModal({
    component: RegistrationNotCompletedModal
})

const { open: openSignInModal } = useModal({
    component: SignInModal
})

async function checkForPendingRequest() {
    let result = false

    if (userId.value && props.project.id) {
        result = await doesPendingParticipationRequestExist(props.project.id, userId.value)
    } else {
        result = false
    }

    hasPendingRequest.value = result
    return result
}

async function handleProjectRequestModal(profileCheck: CompletenessResult) {
    // Ensure we have the required data before opening modal
    if (!props.project || !props.project.id) {
        console.error('Cannot open project request modal: project data missing')
        return
    }

    // Use Exponea if available
    if (process.client && window.$exponea) {
        window.$exponea.track('cta1_zaujem_o_projekt', {})
    }

    if (!auth.currentUser?.emailVerified || !userPrivateProfile.value?.rulesAccepted) {
        openRegistrationNotCompletedModal()
        return
    }

    console.log('Opening modal with project:', props.project)
    console.log('Profile check:', profileCheck)

    // Create modal dynamically with correct props
    const { open, close } = useModal({
        component: ProjectRequestModal,
        attrs: {
            project: props.project,
            profileCheck: profileCheck,
            onConfirm: (sent: boolean) => {
                if (sent) {
                    checkForPendingRequest()
                }
                close()
            }
        }
    })

    await open()
}

async function handleVolunteerWeekProjectRequestModal() {
    if (!props.project || !props.project.id) {
        console.error('Cannot open volunteering week project request modal: project data missing')
        return
    }

    const { open, close } = useModal({
        component: VolunteerWeekProjectRequestModal,
        attrs: {
            project: props.project,
            org: props.org,
            onClose: (confirmed: boolean) => {
                if (confirmed) {
                    checkForPendingRequest()
                }
                close()
            }
        }
    })

    await open()
}

function openLogInModal() {
    if (process.client) {
        localStorage.setItem('beforeLogInLocation', window.location.pathname)
    }

    const { open } = useModal({
        component: SignInModal,
        attrs: {
            message: `Aby organizácia vedela, kto má záujem o jej projekt,
                <strong>prihlás sa</strong> a skontroluj, či máš poriadne
                vyplnený svoj GROWNi profil.`
        }
    })

    open()
}

function checkCompleteness(): CompletenessResult {
    try {
        const profile = userProfile.value
        const volunteerProfile = profile?.volunteerProfile

        if (!profile) {
            return {
                isComplete: false,
                errors: {
                    userProfile: {},
                    volunteerProfile: {}
                }
            }
        }

        const userCompleted = checkUserProfileComplete(profile)
        const volunteerCompleted = checkVolunteerProfileComplete(volunteerProfile)

        // Ensure we always return valid objects
        const userErrors = userCompleted?.errors || {}
        const volunteerErrors = volunteerCompleted?.errors || {}

        return {
            isComplete: Boolean(userCompleted?.isComplete) && Boolean(volunteerCompleted?.isComplete),
            errors: {
                userProfile: { ...userErrors },
                volunteerProfile: { ...volunteerErrors }
            }
        }
    } catch (error) {
        console.error('Error checking profile completeness:', error)
        return {
            isComplete: false,
            errors: {
                userProfile: {},
                volunteerProfile: {}
            }
        }
    }
}

async function showModal() {
    if (isVolunteeringWeekProjectFull.value) {
        return
    }

    trackCheese('volunteering_request_started', {
        project_id: props.project.id || '',
        initiated_by: 'volunteer',
        signed_in: isLoggedIn.value
    })

    if (isLoggedIn.value) {
        if (props.project.type === ProjectType.VOLUNTEERING_WEEK) {
            await handleVolunteerWeekProjectRequestModal()
        } else {
            const profileCheck = checkCompleteness()
            handleProjectRequestModal(profileCheck)
        }
    } else if (props.project.type === ProjectType.VOLUNTEERING_WEEK) {
        await handleVolunteerWeekProjectRequestModal()
    } else {
        openLogInModal()
    }
}
</script>
